/* Bodycare cart: one write queue, one render owner. No third-party dependencies. */
(function (global) {
  'use strict';
  const giftProperty = '_bodycare_free_gift';
  const identity = item => JSON.stringify([Number(item.variant_id || item.id), Object.entries(item.properties || {}).sort(), item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null]);
  function eligibleGiftAmount(items) {
    return items.reduce((sum, item) => sum + (item.qualifies !== false && !item.properties?.[giftProperty] && !Number(item.line_level_total_discount) && !(item.line_level_discount_allocations || []).some(a => Number(a.amount) > 0) ? Number(item.original_line_price) || 0 : 0), 0);
  }
  function confirmedGift(item, title) {
    if (!item?.properties?.[giftProperty] || item.quantity !== 1 || Number(item.final_line_price) !== 0) return false;
    const amount = (item.discount_allocations || item.line_level_discount_allocations || []).reduce((sum, a) => sum + ((a.discount_application?.title || a.title) === title ? Number(a.amount) || 0 : 0), 0);
    return amount > 0 && amount >= Number(item.original_line_price || amount);
  }
  function syncCartPage(current, next, result) {
    const form = current.querySelector('#cart'); const nextForm = next.querySelector('#cart');
    if (!form || !nextForm) return;
    const isEmpty = !result.cart.item_count;
    const wasEmpty = !result.options.oldCart?.item_count;
    if (wasEmpty !== isEmpty) form.innerHTML = nextForm.innerHTML;
    else for (const selector of ['cart-items', '#cart-summary-and-blocks']) {
      const region = current.querySelector(selector); const fresh = next.querySelector(selector);
      if (region && fresh) region.innerHTML = fresh.innerHTML;
    }
    form.className = nextForm.className;
    const summary = current.querySelector('#cart-summary-and-blocks'); if (summary) summary.hidden = isEmpty;
    const css = next.querySelector('#cart-summary-css');
    if (css && !current.querySelector('#cart-summary-css')) current.appendChild(css.cloneNode(true));
  }
  function createCartCoordinator(options) {
    const now = () => typeof performance !== 'undefined' ? performance.now() : Date.now();
    let cart = options.cart || null; let revision = 0; let active = false; let holds = 0; let needsReconciliation = false;
    const queue = []; const quantities = new Map(); const knownKeys = new Map(); const idle = [];
    const root = `${options.root || '/'}`.replace(/\/?$/, '/');
    const pending = () => active || holds > 0 || queue.length > 0;
    const track = () => (cart?.items || []).forEach(item => knownKeys.set(item.key, identity(item)));
    track();
    const notify = () => {
      options.onPending?.(pending());
      if (!pending()) idle.splice(0).forEach(resolve => resolve());
    };
    const sections = () => [...new Set(typeof options.sections === 'function' ? options.sections() : options.sections || [])].filter(Boolean).slice(0, 5);
    const read = payload => options.readCart ? options.readCart(payload) : (Array.isArray(payload?.items) && payload.total_price != null ? payload : null);
    async function commit(payload, meta) {
      const next = read(payload);
      if (next) { cart = next; track(); }
      if (!cart || !next) throw new Error('Cart response needs reconciliation.');
      revision += 1;
      const result = { response: payload, cart, revision, options: meta || {} };
      await options.render?.(result);
      needsReconciliation = false;
      result.timings = meta?.responseAt ? { queueMs: meta.requestAt - meta.actionAt, requestMs: meta.responseAt - meta.requestAt, responseToUiMs: now() - meta.responseAt, totalMs: now() - meta.actionAt } : null;
      options.onCommit?.(result);
      return result;
    }
    async function recover(meta) {
      const r = await options.fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!r.ok) throw new Error('Please refresh your bag to confirm its contents.');
      const next = await r.json();
      const ids = sections();
      if (options.recoverySections && ids.length) {
        const s = await options.fetch(`${root}?sections=${encodeURIComponent(ids.join(','))}`, { headers: { Accept: 'application/json' } });
        if (!s.ok) throw new Error('Please refresh your bag to confirm its contents.');
        next.sections = await s.json();
      }
      return commit(next, { ...meta, recovery: true });
    }
    async function execute(job) {
      if (job.kind === 'reconcile') {
        try { return await recover({ source: 'reconcile' }); }
        catch (error) { needsReconciliation = true; throw error; }
      }
      if (needsReconciliation) throw new Error('Refresh your bag to confirm its contents before making another change.');
      const oldCart = cart; let accepted = false;
      let body; const ids = sections();
      const sectionOptions = { sections: ids, sections_url: options.sectionsUrl || '/' };
      if (job.kind === 'quantity') {
        const matches = (cart?.items || []).filter(item => identity(item) === job.identity);
        const line = matches.find(item => item.key === job.key) || (matches.length === 1 ? matches[0] : null);
        if (!line) return { response: {}, cart, revision, removed: true, options: job.meta };
        body = { id: line.key, quantity: job.quantity, ...sectionOptions };
      } else if (typeof FormData !== 'undefined' && job.items instanceof FormData) {
        body = new FormData();
        for (const [key, value] of job.items.entries()) body.append(key, value);
        body.set('sections', ids.join(',')); body.set('sections_url', sectionOptions.sections_url);
      } else body = { items: job.items, ...sectionOptions };
      const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
      const meta = { ...job.meta, kind: job.kind, oldCart, lineKey: job.key, quantity: job.quantity, actionAt: job.created, requestAt: now() };
      try {
        const r = await options.fetch(`${root}cart/${job.kind === 'quantity' ? 'change' : 'add'}.js`, {
          method: 'POST', headers: { Accept: 'application/json', ...(multipart ? {} : { 'Content-Type': 'application/json' }) }, body: multipart ? body : JSON.stringify(body)
        });
        let payload;
        try { payload = await r.json(); } catch { throw new Error('Could not confirm the cart response. Please refresh your bag before trying again.'); }
        meta.responseAt = now();
        if (!r.ok) {
          const error = new Error(typeof payload.description === 'string' ? payload.description : typeof payload.errors === 'string' ? payload.errors : payload.message || 'This cart change could not be applied.');
          error.status = r.status; throw error;
        }
        accepted = true;
        try { return await commit(payload, meta); } catch (renderError) {
          try { return await recover(meta); } catch {
            needsReconciliation = true;
            renderError.accepted = true; throw renderError;
          }
        }
      } catch (error) {
        if ((!error.status || error.status >= 500 || error.status === 408) && !accepted) {
          needsReconciliation = true;
          error.uncertain = true;
          try { await recover(meta); } catch { /* Preserve the uncertain outcome; never repeat the write. */ }
        }
        options.onError?.(error); throw error;
      }
    }
    async function drain() {
      if (active || !queue.length || !queue[0].ready) { notify(); return; }
      const job = queue.shift(); active = true;
      if (job.kind === 'quantity') quantities.delete(job.identity);
      notify();
      try { const result = await execute(job); job.waiters.forEach(w => w.resolve(result)); }
      catch (error) { job.waiters.forEach(w => w.reject(error)); }
      finally { active = false; drain(); notify(); }
    }
    function add(items, meta = {}) {
      return new Promise((resolve, reject) => {
        queue.push({ kind: 'add', items, meta, created: now(), ready: true, waiters: [{ resolve, reject }] }); notify(); drain();
      });
    }
    function setQuantity(key, quantity, meta = {}) {
      const count = Number(quantity);
      if (!Number.isInteger(count) || count < 0) return Promise.reject(new Error('Enter a valid quantity.'));
      const signature = knownKeys.get(key);
      if (!signature) return Promise.resolve({ cart, revision, removed: true, response: {} });
      let job = quantities.get(signature);
      if (!job) {
        job = { kind: 'quantity', key, identity: signature, meta, quantity: count, ready: false, waiters: [] };
        quantities.set(signature, job); queue.push(job);
      }
      job.quantity = count; job.created = now(); clearTimeout(job.timer);
      const promise = new Promise((resolve, reject) => job.waiters.push({ resolve, reject }));
      job.timer = setTimeout(() => { job.ready = true; drain(); }, options.quantityDelay ?? 120);
      notify(); return promise;
    }
    function whenIdle() {
      queue.forEach(job => { clearTimeout(job.timer); job.ready = true; }); drain();
      return (pending() ? new Promise(resolve => idle.push(resolve)) : Promise.resolve()).then(() => {
        if (needsReconciliation) throw new Error('Refresh your bag to confirm its contents before checkout.');
      });
    }
    const service = {
      add, setQuantity, whenIdle,
      get pending() { return pending(); }, get cart() { return cart; }, get revision() { return revision; },
      get requiresReconciliation() { return needsReconciliation; },
      desiredQuantity(key) { return quantities.get(knownKeys.get(key))?.quantity; },
      async transaction(task) { holds += 1; notify(); try { return await task(); } finally { holds -= 1; notify(); } },
      reconcile() {
        return new Promise((resolve, reject) => {
          queue.forEach(job => { clearTimeout(job.timer); job.ready = true; });
          queue.push({ kind: 'reconcile', ready: true, waiters: [{ resolve, reject }] }); notify(); drain();
        });
      },
      async renderExternal(payload, meta) { return commit(payload, meta); }
    };
    return service;
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { createCartCoordinator, eligibleGiftAmount, confirmedGift, syncCartPage }; return; }
  if (global.BodycareCart) return;
  const doc = global.document; const parsed = new WeakMap();
  const emit = (name, detail) => doc.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  function parseSections(payload) {
    if (parsed.has(payload)) return parsed.get(payload);
    const documents = new Map();
    Object.entries(payload.sections || {}).forEach(([id, html]) => { if (html) documents.set(id, new DOMParser().parseFromString(html, 'text/html')); });
    parsed.set(payload, documents); return documents;
  }
  function readSnapshot(root) {
    const node = root.querySelector('[data-bodycare-cart-state]');
    if (!node) return null;
    try { const state = JSON.parse(node.textContent); state.cart.__bodycareGift = state.gift; return state.cart; } catch { return null; }
  }
  function sectionIds() {
    return [...doc.querySelectorAll('[data-bodycare-cart-state], cart-items, #cart-summary')].map(el => el.dataset.section).filter(id => id && id !== 'cart-drawer').concat('cart-icon-bubble');
  }
  const money = amount => new Intl.NumberFormat(doc.documentElement.lang || 'en-IN', { style: 'currency', currency: global.Shopify?.currency?.active || 'INR', maximumFractionDigits: Number(amount) % 100 ? 2 : 0 }).format(Number(amount) / 100);
  function status(message, isError = false) {
    const el = doc.querySelector('[data-bodycare-cart-status]');
    if (el) { el.textContent = message; el.setAttribute('role', isError ? 'alert' : 'status'); }
  }
  function replaceRegion(current, next) {
    if (!current || !next) return;
    current.innerHTML = next.innerHTML;
    if (current.matches('cart-items')) { current.dataset.empty = next.dataset.empty; if (current.dataset.empty === 'false' && !current.bodycareBound) current.init?.(); }
  }
  async function render(result) {
    const documents = parseSections(result.response); const drawer = doc.querySelector('cart-drawer');
    const drawerId = doc.querySelector('[data-bodycare-cart-state]')?.dataset.section;
    const fresh = documents.get(drawerId); const currentFocus = doc.activeElement;
    if (drawer && !fresh) throw new Error('The cart section could not be confirmed. Please refresh your bag.');
    const focusRow = currentFocus?.closest('.cart-item');
    const focusIdentity = focusRow && result.options.oldCart?.items?.find(i => i.key === focusRow.dataset.lineKey);
    const focusName = currentFocus?.name; const scroll = drawer?.querySelector('.drawer__content')?.scrollTop || 0;
    if (fresh && drawer) {
      const oldFirst = result.options.oldCart?.items?.find(i => !i.properties?.[giftProperty])?.product_id;
      const newFirst = result.cart.items?.find(i => !i.properties?.[giftProperty])?.product_id;
      const wasEmpty = !(result.options.oldCart?.item_count); const isEmpty = !result.cart.item_count;
      const nextDrawer = fresh.querySelector('cart-drawer');
      if (!nextDrawer) throw new Error('Drawer section is missing. Please refresh your bag.');
      if (wasEmpty !== isEmpty) replaceRegion(drawer.querySelector('.drawer__content'), nextDrawer.querySelector('.drawer__content'));
      else {
        replaceRegion(drawer.querySelector('cart-items'), nextDrawer.querySelector('cart-items'));
        replaceRegion(drawer.querySelector('#free-shipping-notice'), nextDrawer.querySelector('#free-shipping-notice'));
        for (const selector of ['#cart-promoted-products', '#cart-drawer-media-promotion']) replaceRegion(drawer.querySelector(selector), nextDrawer.querySelector(selector));
        const gift = drawer.querySelector('bodycare-cart-gift'); const nextGift = nextDrawer.querySelector('bodycare-cart-gift');
        if (gift && nextGift) gift.updateFrom?.(nextGift);
        const rec = drawer.querySelector('#cart-recommendations'); const newRec = nextDrawer.querySelector('#cart-recommendations');
        if (String(oldFirst) !== String(newFirst)) {
          if (rec && newRec) rec.replaceWith(newRec);
          else if (!rec && newRec) drawer.querySelector('.drawer__content').append(newRec);
          else rec?.remove();
        }
      }
      const summary = drawer.querySelector('.cart-drawer__summary'); const nextSummary = nextDrawer.querySelector('.cart-drawer__summary');
      if (wasEmpty !== isEmpty) replaceRegion(summary, nextSummary);
      else {
        summary?.querySelectorAll('[data-bodycare-cart-total]').forEach(el => { el.textContent = money(result.cart.total_price); });
        replaceRegion(summary?.querySelector('[data-bodycare-cart-discounts]'), nextSummary?.querySelector('[data-bodycare-cart-discounts]'));
      }
      drawer.querySelectorAll('[data-bodycare-cart-count]').forEach(el => { el.textContent = result.cart.item_count; });
      drawer.cartSummary = summary;
      const content = drawer.querySelector('.drawer__content'); if (content) content.scrollTop = scroll;
      if (focusIdentity && focusName) {
        const nextLine = result.cart.items.find(i => identity(i) === identity(focusIdentity));
        const nextRow = [...drawer.querySelectorAll('.cart-item')].find(el => el.dataset.lineKey === nextLine?.key);
        nextRow?.querySelector(`[name="${CSS.escape(focusName)}"]`)?.focus({ preventScroll: true });
      }
    } else if (!drawer) {
      for (const [id, root] of documents) {
        const current = doc.getElementById(`shopify-section-${id}`);
        if (!current || id === drawerId) continue;
        syncCartPage(current, root, result);
      }
    }
    const icon = documents.get('cart-icon-bubble'); if (icon) replaceRegion(doc.getElementById('cart-icon-bubble'), icon.querySelector('.shopify-section') || icon.body);
    if (fresh) replaceRegion(doc.querySelector('[data-bodycare-cart-state]'), fresh.querySelector('[data-bodycare-cart-state]'));
    doc.querySelectorAll('.cart-item[data-line-key]').forEach(row => {
      const desired = service.desiredQuantity(row.dataset.lineKey); const input = row.querySelector('.qty-input__input');
      if (input && desired != null) input.value = desired;
    });
    status(result.options.recovery ? 'Your bag has been refreshed.' : 'Bag updated.');
    if (result.options.openCart && drawer?.getAttribute('aria-hidden') !== 'false') drawer?.open?.();
  }
  const service = createCartCoordinator({
    cart: readSnapshot(doc), root: global.Shopify?.routes?.root || '/', sections: sectionIds, sectionsUrl: global.location.pathname, recoverySections: true,
    fetch: global.fetch.bind(global),
    readCart(payload) { for (const root of parseSections(payload).values()) { const cart = readSnapshot(root); if (cart) return cart; } return null; },
    render,
    onPending(pending) { doc.documentElement.classList.toggle('bodycare-cart-pending', pending); doc.querySelectorAll('[data-bodycare-cart-total]').forEach(el => el.setAttribute('aria-busy', String(pending))); global.BodycareGoKwik?.syncButtons?.(); emit('bodycare:cart-pending', { pending }); },
    onCommit(result) {
      if (result.timings) {
        global.__bodycareCartMetrics = global.__bodycareCartMetrics || [];
        global.__bodycareCartMetrics.push({ operation: result.options.kind, source: result.options.source, ...result.timings });
        if (global.__bodycareCartMetrics.length > 100) global.__bodycareCartMetrics.shift();
      }
      emit('bodycare:cart-content:rendered', result);
      if (result.options.kind) {
        const items = result.cart.items || []; const oldItems = result.options.oldCart?.items || [];
        const changed = items.find(i => oldItems.find(o => identity(o) === identity(i))?.quantity !== i.quantity);
        const previous = oldItems.find(i => i.key === result.options.lineKey) || oldItems.find(i => changed && identity(i) === identity(changed));
        const detail = { cart: result.cart, variantId: changed?.variant_id || previous?.variant_id, oldQuantity: previous?.quantity || 0, newQuantity: changed?.quantity || 0 };
        emit(result.options.kind === 'quantity' || previous ? 'on:line-item:change' : 'on:cart:add', detail);
        if (result.options.source === 'bundle') ['cart:refresh', 'cart:updated', 'bodycare:cart-updated'].forEach(name => emit(name, { source: 'bundle', response: result.response }));
      }
      global.BodycareGoKwik?.syncButtons?.();
    },
    onError(error) { status(error.uncertain ? 'The change may have completed. Check your bag before adding again.' : error.message, true); emit('on:cart:error', { error: error.message, uncertain: !!error.uncertain }); }
  });
  service.money = money; service.status = status; service.confirmedGift = confirmedGift;
  global.BodycareCart = service;
  function bridge() {
    const api = global.BodycareTheme;
    if (!api || api.addItems?.bodycareCoordinated) return;
    const addItems = (items, options = {}) => { if (options.openCart) doc.querySelector('cart-drawer')?.open?.(); return service.add(items, { ...options, openCart: options.openCart, source: options.source || 'bodycare' }).then(result => result.response); };
    addItems.bodycareCoordinated = true; api.addItems = addItems;
  }
  doc.addEventListener('load', bridge, true); doc.addEventListener('DOMContentLoaded', bridge); doc.addEventListener('bodycare:content:loaded', bridge); bridge();
  global.addEventListener('pageshow', event => { if (event.persisted) service.reconcile().catch(error => status(error.message, true)); });
  doc.addEventListener('shopify:section:load', event => {
    const state = event.target.querySelector('[data-bodycare-cart-state]');
    if (state) service.renderExternal({ sections: { [state.dataset.section]: event.target.outerHTML } }, { source: 'theme-editor', oldCart: service.cart }).catch(error => status(error.message, true));
    bridge();
  });
})(typeof window === 'undefined' ? globalThis : window);
